import { useCallback, useEffect, useState } from 'react';
import { Activity, AlertTriangle, CheckCircle2, Database, Download, HardDrive, KeyRound, Loader2, RefreshCw, ShieldCheck } from 'lucide-react';
import { toast } from 'sonner';

import api from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';

const STATUS_STYLE = {
  healthy: 'status-success',
  warning: 'status-warning',
  critical: 'status-danger',
};

export default function MonitoringPage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const response = await api.get('/system/monitoring');
      setData(response.data);
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Gagal memuat status monitoring.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const downloadBundle = async () => {
    setDownloading(true);
    try {
      const response = await api.get('/system/support-bundle', { responseType: 'blob' });
      const disposition = response.headers['content-disposition'] || '';
      const filename = disposition.match(/filename="?([^";]+)"?/i)?.[1] || 'fpccs-support.json';
      const url = URL.createObjectURL(response.data);
      const anchor = document.createElement('a');
      anchor.href = url;
      anchor.download = filename;
      anchor.click();
      URL.revokeObjectURL(url);
      toast.success('Support bundle aman berhasil diunduh.');
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Support bundle gagal diunduh.');
    } finally {
      setDownloading(false);
    }
  };

  if (loading && !data) return <div className="space-y-4">{[...Array(5)].map((_, index) => <Skeleton key={index} className="h-28 rounded-xl" />)}</div>;

  const status = data?.overall_status || 'critical';
  return <div className="animate-in-up space-y-6">
    <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
      <div>
        <h1 className="flex items-center gap-2 text-3xl font-semibold tracking-tight"><ShieldCheck className="h-7 w-7 text-[var(--fpccs-primary)]" /> Keamanan & Monitoring</h1>
        <p className="mt-1 text-sm text-muted-fpccs">Ringkasan kesehatan operasional, keamanan, backup, lisensi, dan updater.</p>
      </div>
      <div className="flex gap-2">
        <Button variant="outline" onClick={downloadBundle} disabled={downloading}>{downloading ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <Download className="mr-1.5 h-4 w-4" />} Support Bundle</Button>
        <Button size="icon" variant="ghost" onClick={load} disabled={loading}><RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} /></Button>
      </div>
    </div>

    <div className={`flex items-center gap-3 rounded-xl border p-4 ${STATUS_STYLE[status]}`}>
      {status === 'healthy' ? <CheckCircle2 className="h-5 w-5" /> : <AlertTriangle className="h-5 w-5" />}
      <div><div className="font-semibold">{status === 'healthy' ? 'Semua sistem utama sehat' : status === 'warning' ? 'Ada hal yang perlu diperhatikan' : 'Ada masalah operasional penting'}</div><div className="mt-0.5 text-xs opacity-80">Diperiksa {formatDate(data?.checked_at)}</div></div>
    </div>

    <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-6">
      <StatusCard icon={Database} label="Database" value={data?.database?.connected ? 'Terhubung' : 'Bermasalah'} ok={data?.database?.connected} />
      <StatusCard icon={Activity} label="Migrasi" value={data?.migrations?.is_current ? 'Terkini' : 'Tertunda'} ok={data?.migrations?.is_current} />
      <StatusCard icon={HardDrive} label="Backup terakhir" value={formatRelative(data?.backup?.latest_created_at)} ok={!!data?.backup?.latest_created_at} />
      <StatusCard icon={RefreshCw} label="Updater agent" value={data?.updater?.online ? 'Online' : 'Offline'} ok={data?.updater?.online} />
      <StatusCard icon={KeyRound} label="Lisensi" value={data?.license?.status === 'active' ? `Aktif · ${String(data?.license?.edition || '').toUpperCase()}` : 'Tidak aktif'} ok={data?.license?.status === 'active'} />
      <StatusCard icon={HardDrive} label="Pemakaian disk" value={`${data?.storage?.used_percent ?? '-'}%`} ok={(data?.storage?.used_percent || 0) < 80} />
    </div>

    <div className="grid gap-5 xl:grid-cols-2">
      <section className="card-fpccs p-5">
        <h2 className="font-semibold">Peringatan aktif</h2>
        <div className="mt-4 space-y-3">
          {data?.alerts?.length ? data.alerts.map((alert) => <div key={alert.code} className={`rounded-lg border p-3 ${STATUS_STYLE[alert.severity === 'info' ? 'healthy' : alert.severity]}`}><div className="text-sm font-medium">{alert.title}</div><div className="mt-1 text-xs opacity-80">{alert.message}</div></div>) : <div className="status-success rounded-lg border p-4 text-sm">Tidak ada peringatan aktif.</div>}
        </div>
      </section>

      <section className="card-fpccs p-5">
        <h2 className="font-semibold">Ringkasan keamanan</h2>
        <div className="mt-4 divide-y divide-[var(--fpccs-border)] text-sm">
          <InfoRow label="Login gagal (24 jam)" value={data?.security?.failed_logins_last_24h ?? 0} />
          <InfoRow label="Pembatasan login" value={data?.security?.rate_limit_enabled ? `${data.security.login_max_attempts} percobaan / ${data.security.login_window_minutes} menit` : 'Nonaktif'} />
          <InfoRow label="Dokumentasi API publik" value={data?.security?.api_docs_enabled ? 'Aktif' : 'Nonaktif'} />
          <InfoRow label="Retensi audit log" value={`${data?.security?.audit_retention_days ?? '-'} hari`} />
          <InfoRow label="Update gagal (24 jam)" value={data?.updates?.failed_last_24h ?? 0} />
          <InfoRow label="Jumlah user / project" value={`${data?.usage?.users ?? 0} / ${data?.usage?.projects ?? 0}`} />
        </div>
      </section>
    </div>

    <div className="rounded-xl border border-[var(--fpccs-border)] bg-[var(--fpccs-surface-2)] p-4 text-xs text-muted-fpccs">
      Support bundle hanya memuat metadata diagnostik. Password, secret JWT, connection URL database, encryption key, updater secret, isi database, dan identitas Audit Log tidak disertakan.
    </div>
  </div>;
}

function StatusCard({ icon: Icon, label, value, ok }) { return <div className="card-fpccs p-4"><Icon className={`h-4 w-4 ${ok ? 'text-emerald-400' : 'text-amber-400'}`} /><div className="mt-3 text-[11px] uppercase tracking-wider text-muted-fpccs">{label}</div><div className={`mt-1 text-sm font-semibold ${ok ? 'text-emerald-400' : 'text-amber-400'}`}>{value}</div></div>; }
function InfoRow({ label, value }) { return <div className="flex items-center justify-between gap-4 py-3"><span className="text-muted-fpccs">{label}</span><span className="text-right font-medium">{value}</span></div>; }
function formatDate(value) { return value ? new Intl.DateTimeFormat('id-ID', { dateStyle: 'medium', timeStyle: 'short' }).format(new Date(value)) : '-'; }
function formatRelative(value) { if (!value) return 'Belum ada'; const hours = Math.max(0, Math.floor((Date.now() - new Date(value).getTime()) / 3600000)); return hours < 1 ? '< 1 jam' : hours < 24 ? `${hours} jam` : `${Math.floor(hours / 24)} hari`; }
